import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ListChecks, Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const TERMS = ["Term 1", "Term 2", "Annual"];
const emptyForm = { title: "", description: "", term: "Term 1", order: 0 };

export default function Curriculum() {
  const { user } = useAuth();
  const canEdit = hasPerm(user, "manage_settings");
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [classId, setClassId] = useState("");
  const [subjectId, setSubjectId] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    api.get("/classes").then((r) => setClasses(r.data)).catch(() => {});
    api.get("/subjects").then((r) => setSubjects(r.data)).catch(() => {});
  }, []);

  const load = () => {
    if (!classId) { setItems([]); return; }
    setLoading(true);
    api.get("/curriculum", { params: { class_id: classId, subject_id: subjectId } })
      .then((r) => setItems(r.data)).catch(() => setItems([])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [classId, subjectId]);

  const openNew = () => { setEdit(null); setForm({ ...emptyForm, order: items.length }); setOpen(true); };
  const openEdit = (it) => { setEdit(it); setForm({ title: it.title, description: it.description || "", term: it.term || "Term 1", order: it.order || 0 }); setOpen(true); };
  const save = async () => {
    if (!classId) { toast.error("Select a class first"); return; }
    if (!form.title.trim()) { toast.error("Topic title is required"); return; }
    const payload = { ...form, class_id: classId, subject_id: subjectId, order: Number(form.order) || 0 };
    try {
      if (edit) await api.put(`/curriculum/${edit.id}`, payload);
      else await api.post("/curriculum", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const remove = async (it) => {
    if (!window.confirm(`Delete topic "${it.title}"?`)) return;
    try { await api.delete(`/curriculum/${it.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const subjName = (id) => subjects.find((s) => s.id === id)?.name || "";

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold text-slate-900 flex items-center gap-2"><ListChecks className="text-indigo-500" size={26} />Curriculum</h1>
          <p className="text-slate-500">Define topics and units for each class and subject.</p>
        </div>
        {canEdit && <Button onClick={openNew} disabled={!classId} className="gap-1.5" data-testid="add-curriculum-btn"><Plus size={16} />Add Topic</Button>}
      </div>

      <div className="flex flex-wrap gap-3">
        <Select value={classId} onValueChange={setClassId}>
          <SelectTrigger className="w-56" data-testid="curriculum-class-select"><SelectValue placeholder="Select class" /></SelectTrigger>
          <SelectContent>{classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.section ? `-${c.section}` : ""}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={subjectId || "all"} onValueChange={(v) => setSubjectId(v === "all" ? "" : v)}>
          <SelectTrigger className="w-56" data-testid="curriculum-subject-select"><SelectValue placeholder="All subjects" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All subjects</SelectItem>
            {subjects.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {!classId ? <div className="py-16 text-center text-slate-400">Select a class to view its curriculum.</div> :
          loading ? <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-indigo-500" /></div> :
            items.length === 0 ? <div className="py-16 text-center text-slate-400" data-testid="curriculum-empty">No topics yet.</div> : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                  <tr><th className="px-4 py-2 text-left w-12">#</th><th className="px-4 py-2 text-left">Topic / Unit</th><th className="px-4 py-2 text-left">Subject</th><th className="px-4 py-2 text-left">Term</th>{canEdit && <th className="px-4 py-2"></th>}</tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {items.map((it, i) => (
                    <tr key={it.id} data-testid={`curriculum-row-${it.id}`}>
                      <td className="px-4 py-2.5 text-slate-400">{i + 1}</td>
                      <td className="px-4 py-2.5">
                        <div className="font-medium text-slate-800">{it.title}</div>
                        {it.description && <div className="text-xs text-slate-500 mt-0.5 whitespace-pre-wrap">{it.description}</div>}
                      </td>
                      <td className="px-4 py-2.5 text-slate-600">{subjName(it.subject_id) || "—"}</td>
                      <td className="px-4 py-2.5"><span className="text-xs px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600">{it.term || "—"}</span></td>
                      {canEdit && <td className="px-4 py-2.5 text-right"><div className="flex gap-1 justify-end">
                        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(it)} data-testid={`edit-curriculum-${it.id}`}><Pencil size={15} /></Button>
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-500" onClick={() => remove(it)} data-testid={`delete-curriculum-${it.id}`}><Trash2 size={15} /></Button>
                      </div></td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="curriculum-dialog">
          <DialogHeader>
            <DialogTitle>{edit ? "Edit Topic" : "Add Topic"}</DialogTitle>
            <DialogDescription>Topic for the selected class{subjectId ? ` and ${subjName(subjectId)}` : ""}.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Unit 1: Algebra" className="mt-1" data-testid="curriculum-title-input" /></div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} placeholder="Subtopics, learning outcomes..." className="mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Term</Label>
                <Select value={form.term} onValueChange={(v) => setForm({ ...form, term: v })}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{TERMS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><Label>Order</Label><Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className="mt-1" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-curriculum-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
